/**
 * The re-keyed skill motions (C0-VFX, ported by C0-VFX-PREP; were `ink*` in apps/c0-vfx, renamed `wx*` once the ink look
 * went; docs/research/C0_VFX.md §12): the moves re-keyed with enough
 * keys to read (anticipation dip, a held wind-up, the strike, an overshoot, the settle back into the
 * stance), blended by the skeleton between keys with the eases listed (no kinks at the keys).
 * Played through `SKILL_SWINGS` (render/skillFx.ts); long ones are sampled at sub-frames (motions/wxPhase.ts).
 */
import type { LimbSpec, MotionDef, PoseRigs } from './kit';

/** This group's poses. */
export type Poses =
  | 'wxSink'
  | 'wxCoil'
  | 'wxPass'
  | 'wxCutDeep'
  | 'wxFollowUp'
  | 'wxLowCoil'
  | 'wxRiseMid'
  | 'wxRiseHigh'
  | 'wxRiseOver'
  | 'wxOverCoil'
  | 'wxOverHold'
  | 'wxChop'
  | 'wxChopDeep'
  | 'wxChopHold'
  | 'wxPalmCoil'
  | 'wxPalmMid'
  | 'wxPalmOver'
  | 'wxPalmHold'
  | 'wxQiCoil'
  | 'wxQiDraw'
  | 'wxQiFling'
  | 'wxQiOver'
  | 'wxBreathIn'
  | 'wxGatherHigh'
  | 'wxGatherHold'
  | 'wxDantian'
  | 'wxDantianHold'
  | 'wxUltCoil'
  | 'wxUltRise'
  | 'wxUltPaint1'
  | 'wxUltPaint2'
  | 'wxUltPaint3'
  | 'wxUltPaint4'
  | 'wxUltRaise'
  | 'wxUltCut'
  | 'wxUltDeep'
  | 'wxUltHold'
  | 'wxThrustDraw'
  | 'wxLunge'
  | 'wxLeapCoil'
  | 'wxLeapTop'
  | 'wxSlamDeep'
  | 'wxChestHold'
  | 'wxKneeUp'
  | 'wxKneeHigh'
  | 'wxStompDown'
  | 'wxStompHold'
  | 'wxReel'
  | 'wxReel2'
  | 'wxAir'
  | 'wxKneel'
  | 'wxRecover';

// ── the other moves, re-keyed by kind (render/ink/kinds.ts) ──
const MORE: PoseRigs<
  | 'wxThrustDraw'
  | 'wxLunge'
  | 'wxLeapCoil'
  | 'wxLeapTop'
  | 'wxSlamDeep'
  | 'wxChestHold'
  | 'wxKneeUp'
  | 'wxKneeHigh'
  | 'wxStompDown'
  | 'wxStompHold'
  | 'wxReel'
  | 'wxReel2'
  | 'wxAir'
  | 'wxKneel'
  | 'wxRecover'
> = {
  wxThrustDraw: () => {
    // The blade drawn back level at the hip, point on the foe, the weight on the back leg (held).
    return { o: 5, lean: -2, tilt: -4, headDx: -1, f1: -10, f2: 4, arm: 'lowBack', far: 'fwd', face: 'block', sway: -0.8 };
  },
  wxLunge: () => {
    // Overextended thrust: the longest lunge, the arm and blade one line past the foe.
    return { o: 7, lean: 5, tilt: 18, headDx: 1, f1: -11, f2: 10, arm: 'wxLunge', far: 'back', face: 'atk', sway: 2.5 };
  },
  wxLeapCoil: () => {
    // Crouched deepest before the spring, the blade low behind.
    return { o: 9, lean: 1, tilt: 20, f1: -7, f2: 5, arm: 'back', far: 'fwd', face: 'block', sway: -1.2 };
  },
  wxLeapTop: () => {
    // The top of the leap: knees tucked, the blade raised high and back.
    return { o: 0, tilt: -10, f1: -4, f2: 3, l1: 9, l2: 11, hem: 4, flare: 3, sway: -1.5, arm: 'wxBack', far: 'up', face: 'atk' };
  },
  wxSlamDeep: () => {
    // Landed through the blow: the deepest stance, the blade down before the front foot.
    return { o: 9, lean: 3, tilt: 22, headDx: 1, f1: -9, f2: 8, arm: 'wxDeep', far: 'back', face: 'atk', sway: 2.5 };
  },
  wxChestHold: () => {
    // The buff held: hands before the chest, the body settled, eyes shut.
    return { o: 3, f1: -2, f2: 2, arm: 'chest', far: 'clasp', face: 'cast', closed: true };
  },
  // ── the stamp of the golden dragon palm's buff (dragonPalm/) ──
  wxKneeUp: () => {
    // The front knee drawn up, arms opened for balance.
    return { o: 1, tilt: -3, f1: -3, f2: 4, l2: 7, arm: 'out', far: 'up', face: 'cast', sway: -0.5 };
  },
  wxKneeHigh: () => {
    // Knee at its highest, the body rising onto the standing foot (held a beat).
    return { o: 0, tilt: -5, f1: -3, f2: 5, l2: 10, hem: 2, arm: 'out', far: 'up', face: 'atk', sway: -0.8 };
  },
  wxStompDown: () => {
    // The foot stamped down: the body dropped hard into a wide stance, hands driven down before the belly.
    return { o: 8, tilt: 6, f1: -8, f2: 7, hem: 2, flare: 2, arm: 'chest', far: 'clasp', face: 'atk', sway: 1 };
  },
  wxStompHold: () => {
    // Held in the stance as the dragons rise.
    return { o: 7, tilt: 4, f1: -8, f2: 7, arm: 'chest', far: 'clasp', face: 'cast', sway: 0.5 };
  },
  // ── the receiving side of a lock-on form (ท่าล็อกเป้า): reeling, knocked up, kneeling, recovering ──
  wxReel: () => {
    // Thrown back by a blow: the spine bent far back, arms flung, the front foot lifted.
    // The free arm flung back, not up: seen from behind on the left it would cross the head (depth check, C0-POSE-FIX).
    return { o: 3, lean: -4, tilt: -20, headDx: -1, f1: -7, f2: 1, l2: 2, arm: 'flail', far: 'back', face: 'block', sway: -2 };
  },
  wxReel2: () => {
    // Reeling the other way, arms dropped, staggering.
    return { o: 4, lean: -3, tilt: -12, f1: -5, f2: 3, arm: 'hang', far: 'back', face: 'block', sway: -1.5 };
  },
  wxAir: () => {
    // Knocked up into the air: arched back, legs off the ground, robe flaring.
    return { o: 0, tilt: -24, headDx: -1, f1: -2, f2: 4, l1: 6, l2: 9, hem: 4, flare: 3, arm: 'flail', far: 'up', face: 'block', sway: -2.5 };
  },
  wxKneel: () => {
    // Slammed down to one knee, head bowed.
    return { o: 13, tilt: 18, headDx: 1, f1: -6, f2: 6, arm: 'hang', far: 'hang', face: 'block', closed: true };
  },
  wxRecover: () => {
    // Rising out of the kneel, the guard coming back up.
    return { o: 6, tilt: 8, f1: -6, f2: 4, arm: 'guard', face: 'block' };
  },
};

export const POSES: PoseRigs<Poses> = {
  ...MORE,
  // ── shared ──
  wxSink: () => {
    // Anticipation: the weight dropped a little into the stance before the move gathers.
    return { o: 4, lean: 0, tilt: 4, f1: -7, f2: 5, arm: 'cut', far: 'fwd', face: 'block', sway: 0.3 };
  },
  // ── slash (the basic two-cut chain) ──
  wxCoil: () => {
    // Coiled further than the wind-up: the blade back past the head, the body turned away (held).
    return { o: 4, lean: -3, tilt: -11, headDx: -1, f1: -10, f2: 4, arm: 'wxBack', far: 'fwd', face: 'block', sway: -1 };
  },
  wxPass: () => {
    // The blade passing over: the body unwinding forward, the arm high and leading.
    return { o: 3, lean: 1, tilt: 3, f1: -9, f2: 6, arm: 'overhead', far: 'fwd', face: 'atk', sway: 0.5 };
  },
  wxCutDeep: () => {
    // Overshoot past the cut: lower, the blade carried down and through, the weight on the front foot.
    return { o: 7, lean: 4, tilt: 18, headDx: 1, f1: -10, f2: 9, arm: 'low', far: 'back', face: 'atk', sway: 2 };
  },
  wxFollowUp: () => {
    // The settle: rising out of the follow-through, the blade coming back to guard.
    return { o: 4, lean: 1, tilt: 6, f1: -8, f2: 6, arm: 'cut', far: 'fwd', face: 'idle', sway: 0.8 };
  },
  wxLowCoil: () => {
    // The low guard sunk deeper, blade trailing behind the hip: the coil for a rising cut.
    return { o: 7, lean: -1, tilt: 12, f1: -10, f2: 5, arm: 'lowBack', far: 'fwd', face: 'block', sway: -0.5 };
  },
  wxRiseMid: () => {
    // Driving up: the blade level through the target.
    return { o: 5, lean: 3, tilt: 12, headDx: 1, f1: -9, f2: 8, arm: 'thrust', far: 'back', face: 'atk', sway: 1.5 };
  },
  wxRiseHigh: () => {
    // Swept on up: the blade rising above the head, the body lifting off the back leg.
    return { o: 2, lean: 2, tilt: 0, f1: -8, f2: 8, arm: 'upcut', far: 'back', face: 'atk', sway: 1 };
  },
  wxRiseOver: () => {
    // Overshoot at the top, the back heel lifting.
    return { o: 1, lean: 1, tilt: -4, f1: -7, f2: 8, l1: 1, arm: 'wxHigh', far: 'back', face: 'atk', sway: 0.5 };
  },
  // ── break (an overhead chop raised high: 破 reads as a raised blow) ──
  wxOverCoil: () => {
    // The blade raised high and drawn back, the body arched: the break's wind-up, held to read.
    return { o: 1, lean: -2, tilt: -10, headDx: -1, f1: -9, f2: 4, l1: 1, arm: 'wxBack', far: 'up', face: 'cast', sway: -1 };
  },
  wxOverHold: () => {
    // The held wind-up a hair tighter (a tremble of gathered force).
    return { o: 2, lean: -2, tilt: -12, headDx: -1, f1: -9, f2: 4, l1: 1, arm: 'wxBack', far: 'up', face: 'cast', sway: -1.2 };
  },
  wxChop: () => {
    // The chop meeting the guard: the blade level at the foe's head, everything driving in.
    return { o: 5, lean: 4, tilt: 16, headDx: 1, f1: -10, f2: 9, arm: 'cut', far: 'back', face: 'atk', sway: 2 };
  },
  wxChopDeep: () => {
    // Through the guard and down: the deepest stance, the blade low before the front foot.
    return { o: 8, lean: 4, tilt: 22, headDx: 1, f1: -11, f2: 10, arm: 'wxDeep', far: 'back', face: 'atk', sway: 2.5 };
  },
  wxChopHold: () => {
    // Held low a beat after the break (the zanshin), the blade still down.
    return { o: 7, lean: 3, tilt: 18, headDx: 1, f1: -11, f2: 10, arm: 'low', far: 'back', face: 'atk', sway: 1.5 };
  },
  // ── palm ──
  wxPalmCoil: () => {
    // Palm drawn back past the hip, the body turned away and low: the coil.
    return { o: 6, lean: -3, tilt: -8, headDx: -1, f1: -10, f2: 3, arm: 'back', far: 'fwd', face: 'block', sway: -1 };
  },
  wxPalmMid: () => {
    // The palm on its way, at the chest, the weight shifting forward.
    return { o: 5, lean: 1, tilt: 4, f1: -9, f2: 6, arm: 'chest', far: 'hip', face: 'atk', sway: 0.5 };
  },
  wxPalmOver: () => {
    // Overextended at the strike: the longest bow stance, the palm driven past the foe's chest.
    return { o: 7, lean: 5, tilt: 18, headDx: 1, f1: -11, f2: 10, arm: 'wxPalm', far: 'hip', face: 'atk', sway: 2.5 };
  },
  wxPalmHold: () => {
    // The palm held out (the force released into the foe), the body settling back a little.
    return { o: 6, lean: 3, tilt: 12, headDx: 1, f1: -10, f2: 9, arm: 'thrust', far: 'hip', face: 'atk', sway: 1.5 };
  },
  // ── qi (sword qi flung out) ──
  wxQiCoil: () => {
    // The blade drawn low and back behind the hip, the body wound away.
    return { o: 6, lean: -2, tilt: -4, f1: -9, f2: 4, arm: 'lowBack', far: 'fwd', face: 'block', sway: -0.8 };
  },
  wxQiDraw: () => {
    // Drawn across the body to the chest, the free hand pointing (sword fingers) at the foe.
    return { o: 5, lean: 0, tilt: 2, f1: -9, f2: 5, arm: 'wrap', far: 'fwd', face: 'cast', sway: 0 };
  },
  wxQiFling: () => {
    // Flung out level: the blade sweeping open, the body opening after it.
    return { o: 3, lean: 3, tilt: 8, f1: -9, f2: 8, hem: 1, arm: 'wxLevel', far: 'back', face: 'atk', sway: 2 };
  },
  wxQiOver: () => {
    // Past level, the blade swept out and up a little: the overshoot.
    return { o: 3, lean: 3, tilt: 6, f1: -9, f2: 8, hem: 1, arm: 'out', far: 'back', face: 'atk', sway: 1.5 };
  },
  // ── heal (qi drawn in, sunk to the dantian) ──
  wxBreathIn: () => {
    // A breath in: the body rising a little, the arms opening.
    return { o: 2, tilt: -2, f1: -4, f2: 4, arm: 'out', far: 'hang', face: 'cast', closed: true };
  },
  wxGatherHigh: () => {
    // Arms raised high, gathering heaven's qi.
    return { o: 0, tilt: -8, f1: -4, f2: 4, arm: 'raise', far: 'up', face: 'cast', closed: true };
  },
  wxGatherHold: () => {
    // Held high, rising onto the toes a hair.
    return { o: 0, tilt: -7, f1: -4, f2: 4, l1: 1, arm: 'raise', far: 'up', face: 'cast', closed: true };
  },
  wxDantian: () => {
    // Sunk to the dantian: knees bent, hands before the belly.
    return { o: 6, tilt: 2, f1: -5, f2: 5, arm: 'chest', far: 'clasp', face: 'cast', closed: true };
  },
  wxDantianHold: () => {
    // Held, breathing out.
    return { o: 5, tilt: 1, f1: -5, f2: 5, arm: 'chest', far: 'clasp', face: 'cast', closed: true };
  },
  // ── ultimate (painting the character, then the great cut) ──
  wxUltCoil: () => {
    // Deepest coil of rage, the blade low behind.
    return { o: 9, lean: -2, tilt: 22, headDx: 1, f1: -8, f2: 6, arm: 'lowBack', far: 'back', face: 'block', closed: true };
  },
  wxUltRise: () => {
    // Bursting up, the blade overhead, the robe flaring (the roar).
    // The free arm flung back for the roar, not up: seen from behind on the left it crossed the head (depth check).
    return { o: 0, lean: -1, tilt: -14, headDx: -1, f1: -8, f2: 7, l1: 1, hem: 3, flare: 3, arm: 'overhead', far: 'back', face: 'atk', sway: -1 };
  },
  wxUltPaint1: () => {
    // The blade as a brush: a level stroke out to the side.
    return { o: 3, lean: 2, tilt: 6, f1: -8, f2: 7, hem: 2, flare: 2, arm: 'wxLevel', far: 'back', face: 'cast', sway: 1.5 };
  },
  wxUltPaint2: () => {
    // Drawn back across for the next stroke, the blade high.
    return { o: 2, lean: -1, tilt: -4, f1: -8, f2: 6, hem: 2, flare: 2, arm: 'upcut', far: 'fwd', face: 'cast', sway: -1 };
  },
  wxUltPaint3: () => {
    // A falling stroke: the blade swept down across the body.
    return { o: 4, lean: 2, tilt: 10, f1: -9, f2: 7, hem: 2, flare: 2, arm: 'cut', far: 'back', face: 'cast', sway: 1.5 };
  },
  wxUltPaint4: () => {
    // The last stroke flicked up and out.
    return { o: 3, lean: 1, tilt: 2, f1: -8, f2: 7, hem: 2, flare: 2, arm: 'out', far: 'up', face: 'cast', sway: 0.5 };
  },
  wxUltRaise: () => {
    // The blade raised high and back for the great cut, held.
    return { o: 2, lean: -2, tilt: -12, headDx: -1, f1: -9, f2: 5, l1: 1, hem: 2, arm: 'wxBack', far: 'up', face: 'atk', sway: -1.5 };
  },
  wxUltCut: () => {
    // The great cut lands: the longest lunge.
    return { o: 7, lean: 4, tilt: 22, headDx: 1, f1: -11, f2: 10, hem: 1, sway: 2.5, arm: 'cut', far: 'back', face: 'atk' };
  },
  wxUltDeep: () => {
    // Carried through, the blade down before the front foot.
    return { o: 9, lean: 4, tilt: 24, headDx: 1, f1: -11, f2: 10, hem: 1, sway: 2.5, arm: 'wxDeep', far: 'back', face: 'atk' };
  },
  wxUltHold: () => {
    // Held in the lunge, the blade still low (the brush lifted from the paper).
    return { o: 7, lean: 3, tilt: 18, headDx: 1, f1: -11, f2: 10, sway: 1.5, arm: 'low', far: 'back', face: 'atk', closed: true };
  },
};


export const MOTIONS = [
  /** Basic first cut: dip, coil (held), over the top, the cut, overshoot low, settle back to guard. */
  {
    id: 'wxCleave',
    keys: ['ready', 'wxSink', 'windup', 'wxCoil', 'wxPass', 'cut', 'wxCutDeep', 'follow', 'wxFollowUp', 'ready'],
    ease: ['inOut', 'out', 'inOut', 'in', 'linear', 'out', 'inOut', 'inOut', 'inOut'],
  },
  /** Basic second cut: sunk into the low guard, driven up through level, swept high, settled. */
  {
    id: 'wxRise',
    keys: ['ready', 'lowGuard', 'wxLowCoil', 'wxRiseMid', 'wxRiseHigh', 'wxRiseOver', 'atk', 'wxFollowUp', 'ready'],
    ease: ['out', 'inOut', 'in', 'linear', 'out', 'inOut', 'inOut', 'inOut'],
  },
  /** Break: dip, raised high, coiled and held (the tell), the chop through the guard, deep, held, back. */
  {
    id: 'wxBreak',
    keys: [
      'ready',
      'wxSink',
      'overhead',
      'wxOverCoil',
      'wxOverHold',
      'wxPass',
      'wxChop',
      'wxChopDeep',
      'wxChopHold',
      'follow',
      'wxFollowUp',
      'ready',
    ],
    ease: ['inOut', 'out', 'inOut', 'inOut', 'in', 'linear', 'out', 'inOut', 'inOut', 'inOut', 'inOut'],
  },
  /** Palm: dip, palm drawn to the hip, coiled, driven out past the chest, overextended, held, back. */
  {
    id: 'wxPalm',
    keys: ['ready', 'wxSink', 'palmDraw', 'wxPalmCoil', 'wxPalmMid', 'palmPush', 'wxPalmOver', 'wxPalmHold', 'wxFollowUp', 'ready'],
    ease: ['inOut', 'out', 'inOut', 'in', 'linear', 'out', 'inOut', 'inOut', 'inOut'],
  },
  /** Sword qi: gathered low, drawn across, flung out level, overshoot, settle. */
  {
    id: 'wxQi',
    keys: ['ready', 'wxSink', 'qiGather', 'wxQiCoil', 'wxQiDraw', 'wxQiFling', 'wxQiOver', 'qiRelease', 'wxFollowUp', 'ready'],
    ease: ['inOut', 'out', 'inOut', 'inOut', 'in', 'out', 'inOut', 'inOut', 'inOut'],
  },
  /** Heal: breath in, arms raised, held, sunk to the dantian, held, back to stand. */
  {
    id: 'wxHeal',
    keys: [
      'ready',
      'wxBreathIn',
      'gatherUp',
      'wxGatherHigh',
      'wxGatherHold',
      'gatherUp',
      'gatherDown',
      'wxDantian',
      'wxDantianHold',
      'gatherDown',
      'wxBreathIn',
      'ready',
    ],
    ease: ['inOut', 'inOut', 'inOut', 'inOut', 'inOut', 'inOut', 'inOut', 'inOut', 'inOut', 'inOut', 'inOut'],
  },
  /**
   * Ultimate: sink, rage coil, burst up, the blade painting the character in four strokes, raised
   * and held, the great cut, carried through, held, recovered (22 keys).
   */
  {
    id: 'wxUlt',
    keys: [
      'ready',
      'wxSink',
      'rageCoil',
      'wxUltCoil',
      'wxUltRise',
      'rageRoar',
      'wxUltPaint1',
      'wxUltPaint2',
      'wxUltPaint3',
      'wxUltPaint4',
      'wxUltPaint2',
      'wxUltPaint1',
      'wxUltRaise',
      'wxOverHold',
      'wxPass',
      'wxUltCut',
      'wxUltDeep',
      'wxUltHold',
      'wxChopHold',
      'follow',
      'wxFollowUp',
      'ready',
    ],
    ease: [
      'inOut',
      'out',
      'inOut',
      'out',
      'inOut',
      'inOut',
      'inOut',
      'inOut',
      'inOut',
      'inOut',
      'inOut',
      'out',
      'inOut',
      'in',
      'linear',
      'out',
      'inOut',
      'inOut',
      'inOut',
      'inOut',
      'inOut',
    ],
  },
  /** Thrust: dip, sunk into the low guard, drawn back level (held), driven through, overextended, back. */
  {
    id: 'wxThrust',
    keys: ['ready', 'wxSink', 'lowGuard', 'wxLowCoil', 'wxThrustDraw', 'thrust', 'wxLunge', 'thrust', 'follow', 'ready'],
    ease: ['inOut', 'out', 'inOut', 'inOut', 'in', 'out', 'inOut', 'inOut', 'inOut'],
  },
  /** Area slam: dip, crouch, coil (held), spring, the top, down onto the ground, deepest, settle. */
  {
    id: 'wxSlam',
    keys: ['ready', 'wxSink', 'leapCrouch', 'wxLeapCoil', 'leap', 'wxLeapTop', 'slam', 'wxSlamDeep', 'slam', 'wxFollowUp', 'ready'],
    ease: ['inOut', 'out', 'inOut', 'out', 'out', 'in', 'out', 'inOut', 'inOut', 'inOut'],
  },
  /** Power move: sink, rage coil (held), burst up, roar, raised and held, the great cut, through, held, back (15 keys). */
  {
    id: 'wxPower',
    keys: [
      'ready',
      'wxSink',
      'rageCoil',
      'wxUltCoil',
      'wxUltRise',
      'rageRoar',
      'wxUltRaise',
      'wxOverHold',
      'wxPass',
      'wxUltCut',
      'wxUltDeep',
      'wxUltHold',
      'follow',
      'wxFollowUp',
      'ready',
    ],
    ease: ['inOut', 'out', 'inOut', 'out', 'inOut', 'inOut', 'inOut', 'in', 'linear', 'out', 'inOut', 'inOut', 'inOut', 'inOut'],
  },
  // ── lock-on forms (ท่าล็อกเป้า): a continuous combo on the caster, a matching receive on the target ──
  /** The target's side: hit, reeled, hit again, staggered, knocked up, slammed to a knee, recovering (12 keys). */
  {
    id: 'wxReceive',
    keys: ['ready', 'hurt', 'wxReel', 'hurt', 'wxReel2', 'wxReel', 'wxAir', 'wxReel', 'wxKneel', 'wxRecover', 'idle', 'ready'],
    ease: ['out', 'out', 'inOut', 'out', 'inOut', 'out', 'inOut', 'in', 'inOut', 'inOut', 'inOut'],
  },
  /** Sword lock-on: two cuts, a rising cut that lifts the foe, a lunge, raised and held, the finishing chop (20 keys). */
  {
    id: 'wxLockSword',
    keys: [
      'ready',
      'wxSink',
      'windup',
      'wxPass',
      'cut',
      'wxCutDeep',
      'lowGuard',
      'wxRiseMid',
      'wxRiseHigh',
      'wxThrustDraw',
      'wxLunge',
      'thrust',
      'wxOverCoil',
      'wxOverHold',
      'overhead',
      'wxChop',
      'wxChopDeep',
      'wxChopHold',
      'wxFollowUp',
      'ready',
    ],
    ease: ['inOut', 'out', 'in', 'linear', 'out', 'inOut', 'in', 'out', 'inOut', 'in', 'out', 'inOut', 'inOut', 'in', 'linear', 'out', 'inOut', 'inOut', 'inOut'],
  },
  /** Palm lock-on: palm, palm, an uppercut that lifts the foe, coiled, the finishing palm (15 keys). */
  {
    id: 'wxLockPalm',
    keys: [
      'ready',
      'wxSink',
      'palmDraw',
      'palmPush',
      'wxPalmCoil',
      'wxPalmOver',
      'lowGuard',
      'wxRiseHigh',
      'wxKneeHigh',
      'wxPalmCoil',
      'wxPalmMid',
      'wxPalmOver',
      'wxPalmHold',
      'wxFollowUp',
      'ready',
    ],
    ease: ['inOut', 'out', 'in', 'inOut', 'in', 'inOut', 'in', 'out', 'inOut', 'in', 'linear', 'out', 'inOut', 'inOut'],
  },
  // ── ultimates' showcases (ท่าพลัง, one per art) ──
  /** Sword art (ground-targeted): aura, a leap and flourish, the glyph painted in the air, landing, the great cut (30 keys). */
  {
    id: 'wxUltShow',
    keys: [
      'ready',
      'wxSink',
      'rageCoil',
      'wxUltCoil',
      'rageCoil',
      'wxUltRise',
      'rageRoar',
      'wxLeapCoil',
      'leap',
      'wxLeapTop',
      'wxUltPaint1',
      'wxUltPaint2',
      'wxUltPaint3',
      'wxUltPaint4',
      'wxUltPaint2',
      'wxUltPaint1',
      'slam',
      'wxSlamDeep',
      'wxUltRaise',
      'wxOverHold',
      'wxPass',
      'wxUltCut',
      'wxUltDeep',
      'wxUltHold',
      'wxChopHold',
      'wxSlamDeep',
      'follow',
      'wxFollowUp',
      'wxBreathIn',
      'ready',
    ],
    ease: [
      'inOut', 'out', 'inOut', 'inOut', 'out', 'inOut', 'inOut', 'out', 'out', 'inOut',
      'inOut', 'inOut', 'inOut', 'inOut', 'inOut', 'in', 'out', 'inOut', 'inOut', 'in',
      'linear', 'out', 'inOut', 'inOut', 'inOut', 'inOut', 'inOut', 'inOut', 'inOut',
    ],
  },
  /** Palm art (around the caster): qi gathered high, a leap, palms driven down onto the ground all round, the wave (23 keys). */
  {
    id: 'wxUltPalmShow',
    keys: [
      'ready',
      'wxSink',
      'palmDraw',
      'wxPalmCoil',
      'wxBreathIn',
      'wxGatherHigh',
      'wxGatherHold',
      'wxLeapCoil',
      'leap',
      'wxLeapTop',
      'wxKneeHigh',
      'slam',
      'wxSlamDeep',
      'wxStompDown',
      'wxStompHold',
      'wxPalmCoil',
      'palmPush',
      'wxPalmOver',
      'wxPalmHold',
      'wxChestHold',
      'wxDantian',
      'wxBreathIn',
      'ready',
    ],
    ease: [
      'inOut', 'out', 'inOut', 'inOut', 'inOut', 'inOut', 'out', 'out', 'inOut', 'in', 'in',
      'out', 'inOut', 'inOut', 'inOut', 'in', 'out', 'inOut', 'inOut', 'inOut', 'inOut', 'inOut',
    ],
  },
  /** Dragon palm (ground-targeted): the stamp, dragons called, a leap, roar to the sky, the great dragon brought down (21 keys). */
  {
    id: 'wxUltDragonShow',
    keys: [
      'ready',
      'wxSink',
      'wxKneeUp',
      'wxKneeHigh',
      'wxStompDown',
      'wxStompHold',
      'wxGatherHigh',
      'wxGatherHold',
      'wxLeapCoil',
      'leap',
      'wxLeapTop',
      'rageRoar',
      'slam',
      'wxPalmCoil',
      'wxPalmMid',
      'wxPalmOver',
      'wxPalmHold',
      'wxStompHold',
      'wxChestHold',
      'wxBreathIn',
      'ready',
    ],
    ease: ['inOut', 'out', 'inOut', 'in', 'out', 'inOut', 'inOut', 'out', 'out', 'inOut', 'inOut', 'in', 'inOut', 'in', 'out', 'inOut', 'inOut', 'inOut', 'inOut', 'inOut'],
  },
  /** Stamp (the golden dragon palm's buff): dip, knee up, highest (held), stamped down, held, settle, back. */
  {
    id: 'wxStomp',
    keys: ['ready', 'wxSink', 'wxKneeUp', 'wxKneeHigh', 'wxStompDown', 'wxStompHold', 'wxChestHold', 'wxBreathIn', 'ready'],
    ease: ['inOut', 'out', 'inOut', 'in', 'out', 'inOut', 'inOut', 'inOut'],
  },
  /** Buff: breath in, hands to the chest, held, raised, held, back down, settle. */
  {
    id: 'wxBuff',
    keys: ['ready', 'wxBreathIn', 'chest', 'wxChestHold', 'wxGatherHigh', 'wxChestHold', 'chest', 'wxBreathIn', 'ready'],
    ease: ['inOut', 'inOut', 'inOut', 'inOut', 'inOut', 'inOut', 'inOut', 'inOut'],
  },
] as const satisfies readonly MotionDef[];

/** This group's straight-limb arms: hand (relative to the body's centre, before the drop) and blade angle. */
export const ARMS = {
  /** Blade drawn back over the shoulder past the head, pointing back and up. */
  wxBack: { hand: [-6, 2], ang: -2.35, front: false },
  /** High overshoot of a rising cut: hand above the head, blade up and back. */
  wxHigh: { hand: [6, -1], ang: -1.9, front: true },
  /** After a chop: hand low before the front knee, blade pointing down and forward. */
  wxDeep: { hand: [11, 39], ang: 1.35, front: true },
  /** Blade flung out level to the front. */
  wxLevel: { hand: [15, 23], ang: -0.15, front: true },
  /** A palm driven past the foe's chest. */
  wxPalm: { hand: [17, 26], ang: 0, front: true },
  /** An overextended thrust: hand far forward at chest height, blade level. */
  wxLunge: { hand: [18, 27], ang: 0, front: true },
} satisfies Record<string, LimbSpec>;

/**
 * The ink motions are drawn at quarter frames (renderer.ts `motion`), so a long one (the ultimate's
 * 22 keys) shows every key and its in-betweens rather than 14 samples of it.
 */
export const WX_SUB = 4;
export const WX_MOTION_IDS: ReadonlySet<string> = new Set(MOTIONS.map((m) => m.id));
/** Steps per frame an ink motion is drawn at: quarter frames, eighths for the long showcases (over 20 keys). */
export const wxSub = (id: string): number => (MOTIONS.find((m) => m.id === id)?.keys.length ?? 0) > 20 ? 8 : WX_SUB;
